/**
 * DS Coder — client for the live documentation proxy (/api/github?route=docs).
 *
 * Fetches current library docs (Context7-backed) so answers about a repo's
 * stack reflect today's APIs, not the model's training cutoff. Results are
 * cached in memory + localStorage with a TTL to keep chat latency low.
 */

import type { LiveDocsResult } from './types';

const API_BASE = '';
const CACHE_PREFIX = 'ds_coder_docs:';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const memoryCache = new Map<string, LiveDocsResult>();

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getCurrentSession } = await import('@/lib/supabase');
  const session = await getCurrentSession();
  if (!session) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${session.access_token}` };
}

function cacheKey(libraryId: string, topic?: string): string {
  return `${CACHE_PREFIX}${libraryId}|${topic ?? ''}`;
}

function readCache(key: string): LiveDocsResult | null {
  const mem = memoryCache.get(key);
  if (mem && Date.now() - new Date(mem.fetchedAt).getTime() < CACHE_TTL_MS) return mem;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LiveDocsResult;
    if (Date.now() - new Date(parsed.fetchedAt).getTime() >= CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    memoryCache.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: LiveDocsResult): void {
  memoryCache.set(key, value);
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full/unavailable — memory cache still applies for the session
  }
}

/**
 * Fetch live docs for one library. Returns null on any failure — the caller
 * treats docs as best-effort enrichment, never a blocker for sending a chat.
 */
export async function fetchLiveDocs(
  libraryId: string,
  opts: { topic?: string; tokens?: number; signal?: AbortSignal } = {}
): Promise<LiveDocsResult | null> {
  const key = cacheKey(libraryId, opts.topic);
  const cached = readCache(key);
  if (cached) return cached;

  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ op: 'get', libraryId });
    if (opts.topic) params.set('topic', opts.topic);
    if (opts.tokens) params.set('tokens', String(opts.tokens));
    const response = await fetch(`${API_BASE}/api/github?route=docs&${params.toString()}`, {
      headers,
      signal: opts.signal,
    });
    if (!response.ok) return null;
    const result = (await response.json()) as LiveDocsResult;
    if (!result?.content?.trim()) return null;
    writeCache(key, result);
    return result;
  } catch {
    return null;
  }
}

/** Fetch docs for several libraries concurrently; failures are dropped. */
export async function fetchLiveDocsBatch(
  requests: Array<{ libraryId: string; topic?: string; tokens?: number }>,
  signal?: AbortSignal
): Promise<LiveDocsResult[]> {
  const results = await Promise.all(
    requests.map((r) => fetchLiveDocs(r.libraryId, { ...r, signal }))
  );
  return results.filter((r): r is LiveDocsResult => r !== null);
}
